"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowLeft, CheckCircle, BookOpen, AlertTriangle, Wrench, FileText } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface ComplianceCondition {
  id: string
  conditionNumber: string
  title: string
  description: string
  managementMethod: string
  status: "compliant" | "in-progress" | "pending"
  category?: string
  additionalDetailsUrl?: string // Link to additional details or compliance report
}

const complianceConditions: ComplianceCondition[] = [
  // Limits
  {
    id: "condition-1",
    conditionNumber: "Condition 1",
    title: "Discharge Limits - Equipment Specifications",
    description: "Discharge into air must be only combustion products from the Inciner8 I8-75A Cremator, which: (a) Is powered by diesel; (b) Has a first chamber achieving a temperature in excess of 750 degrees; and (c) Has a second chamber achieving temperature in excess of 850 degrees Celsius.",
    managementMethod: "By design and system-validated - The Inciner8 I8-75A cremator is diesel-powered and designed with dual chambers meeting the specified temperature requirements. Temperature monitoring is conducted during each cremation cycle and recorded in the logbook. The system validates that first chamber temperature exceeds 750°C and second chamber temperature exceeds 850°C when completing batches, ensuring compliance with Condition 1.",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-2",
    conditionNumber: "Condition 2",
    title: "Location Requirements",
    description: "The Inciner8 I8-75A shall be operated at 386 Carrs Road, Loburn, Lot 5 DP 447507 as shown in Plan CRC254775A, at or about map reference NZTM2000 1563319mE, 5213322mN.",
    managementMethod: "By design - The cremator is permanently installed at the specified location as per the resource consent.",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-3",
    conditionNumber: "Condition 3",
    title: "Installation and Stack Requirements",
    description: "The cremator must be installed in a purpose built shed located as shown in Plan CRC254775B, and discharge must occur via stack at a height of at least 4.8 metres above ground level and not less than 1.8 metres above the ridgeline of the shed.",
    managementMethod: "By design - The cremator is installed in a purpose-built shed as specified, with the stack height meeting the minimum requirements of 4.8m above ground level and 1.8m above the shed ridgeline.",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-4",
    conditionNumber: "Condition 4",
    title: "Single Operation Requirement",
    description: "This consent authorises two Inciner8 I8-75A on site. However, only one incinerator is to be operated at any one time.",
    managementMethod: "System-enforced - The cremation batch management system automatically prevents starting a new batch if another machine is currently active. This validation is enforced at both batch creation and start, ensuring only one incinerator operates at any given time. The logbook records which cremator undertook each cremation.",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-5",
    conditionNumber: "Condition 5",
    title: "Vertical Discharge",
    description: "The discharge must be directed vertically into the air and must not be impeded by any obstruction above the stack that decreases the vertical efflux velocity.",
    managementMethod: "By design - The stack is designed and installed to discharge vertically with no obstructions above that would impede vertical efflux velocity.",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-6",
    conditionNumber: "Condition 6",
    title: "Opacity Limits",
    description: "The opacity of the discharge from the stack must not be darker than Ringelmann Shade 1 (except for a period not exceeding two minutes in each hour of operation). When darker, the consent holder must review operation and service requirements.",
    managementMethod: "System-tracked - Opacity readings (Ringelmann Shade) are recorded in the logbook for each cremation batch through the completion form. If opacity exceeds Ringelmann Shade 1, operations are reviewed and service requirements (Condition 17) are checked via the Service Reports system to improve combustion performance. Reference: Ringelmann Smoke Chart (https://www.3ieimpact.org/sites/default/files/2020-03/DPW1.1067-India-Pollution-Online-appendix-C.pdf).",
    status: "compliant",
    category: "Limits",
  },
  {
    id: "condition-7",
    conditionNumber: "Condition 7",
    title: "Odour and Particulate Control",
    description: "The discharge must not cause an odour or deposition of particulate or suspended particulate, which is offensive or objectionable beyond the boundary of the property.",
    managementMethod: "Operational management - The i8-75A Incinerator features dual-chamber combustion with secondary chamber operating at 1100°C for complete burn and odour destruction, integrated afterburner to eliminate VOCs, and DEFRA-approved compliance. All cremation activities are conducted within a fully enclosed shed. Indigenous vegetation has been planted around the site to increase habitats and enable carbon sequestration. A comprehensive Dust Management Plan is implemented with all dust-generating activities conducted indoors. Operations are conducted in accordance with the OMP to minimize odour and particulate emissions. Complaint records (Condition 23) are maintained to track any issues.",
    status: "compliant",
    category: "Limits",
    additionalDetailsUrl: "/api/council/landscaping",
  },
  // Cremator Operation
  {
    id: "condition-8",
    conditionNumber: "Condition 8",
    title: "Operating Hours",
    description: "The Inciner8 I8-75A must be operated only between the hours of 7 am and 7 pm Monday to Saturday. No operation is to occur on Sundays or public holidays.",
    managementMethod: "System-monitored - The cremation batch management system checks NZ time when creating or starting batches. Operations outside 7am-7pm Monday-Saturday or on Sundays trigger a warning that is logged and displayed to the operator. The logbook records the start time and duration of each cremation to verify compliance.",
    status: "compliant",
    category: "Cremator Operation",
    additionalDetailsUrl: "/process", // Link to process page showing operating hours
  },
  {
    id: "condition-9",
    conditionNumber: "Condition 9",
    title: "Authorised Materials",
    description: "This consent authorises the cremation of animals only. The cremator must not be used for: pet accessories, destruction of humans or parts thereof, pathological waste, or materials from laboratories.",
    managementMethod: "Operational procedure - Only animal remains are accepted for cremation. The animal remains acceptance procedure in the OMP (Condition 13) ensures compliance. The logbook (Condition 16) records the type and number of animals in each charge. All ash and residual material is collected, contained, and either returned to pet owners/clinics or securely stored for off-site disposal by an approved waste handler - no disposal occurs on-site.",
    status: "compliant",
    category: "Cremator Operation",
    additionalDetailsUrl: "/council/operational-responses",
  },
  {
    id: "condition-10",
    conditionNumber: "Condition 10",
    title: "Processing of Odorous Materials",
    description: "Processing of all odorous materials or ash waste must be undertaken within the enclosed building.",
    managementMethod: "Operational procedure - All processing of odorous materials and ash waste is conducted within the enclosed purpose-built shed to prevent odour emissions beyond the boundary. The fully enclosed shed structure, combined with the dual-chamber combustion system operating at high temperatures (1100°C secondary chamber), ensures complete odour destruction and prevents emissions beyond the site boundary.",
    status: "compliant",
    category: "Cremator Operation",
    additionalDetailsUrl: "/council/operational-responses",
  },
  {
    id: "condition-11",
    conditionNumber: "Condition 11",
    title: "Charge Weight and Animal Size Limits",
    description: "A cremation charge (total animal weight) shall not exceed 50 kilograms in each cycle, and only small animals (e.g. dogs, cats or smaller) are to be cremated.",
    managementMethod: "System-validated - Total weight is recorded in the logbook for each cremation batch through the completion form. The system validates that weight does not exceed 50kg per cycle. Only small animals (dogs, cats, or smaller) are accepted into the system, and animal types are tracked and displayed in batch records.",
    status: "compliant",
    category: "Cremator Operation",
  },
  {
    id: "condition-12",
    conditionNumber: "Condition 12",
    title: "Processing Timeframe",
    description: "Animal remains delivered to the site shall be processed within 24 hours or frozen for storage.",
    managementMethod: "Operational procedure - Animal remains are either processed within 24 hours of delivery or immediately frozen for storage. This is managed through the animal remains acceptance and storage procedures in the OMP (Condition 13).",
    status: "compliant",
    category: "Cremator Operation",
  },
  // Operation and Management Plan
  {
    id: "condition-13",
    conditionNumber: "Condition 13",
    title: "Operation and Management Plan (OMP) - Development and Certification",
    description: "All site activities must be in accordance with an OMP developed specifically for this site. The OMP must be submitted to and certified by the Canterbury Regional Council prior to exercise of this resource consent.",
    managementMethod: "Documentation - The OMP has been developed, submitted to, and certified by the Canterbury Regional Council. All site activities are conducted in accordance with the certified OMP.",
    status: "compliant",
    category: "Operation and Management Plan",
  },
  {
    id: "condition-14",
    conditionNumber: "Condition 14",
    title: "OMP Content Requirements",
    description: "The OMP must include: (a) Animal remains acceptance procedure; (b) Procedures for storing, transferring, and treating remains; (c) Methods for operating the cremator; (d) Maintenance procedures; (e) Staff training details; (f) Contingency measures; (g) Reporting requirements; and (h) Annual review process.",
    managementMethod: "Complete OMP Documentation - The certified OMP (available in the Operations Plan section) contains all required elements: (a) Detailed animal remains acceptance procedure with criteria and process; (b) Comprehensive procedures for storing (freezer storage), transferring (clinic to facility, facility to delivery), and treating remains (cremation, cremulation, clean room processing); (c) Methods for operating the Inciner8 I8-75A including temperature requirements (750°C+ first chamber, 850°C+ second chamber), operating procedures, and emission control measures; (d) Maintenance procedures including annual servicing requirements and daily operational maintenance; (e) Staff training details covering consent conditions, OMP requirements, logbook procedures, operating/maintenance procedures, and servicing requirements, delivered through the Training Management System; (f) Contingency measures for machine breakdown, power outages, and other unforeseen situations; (g) Reporting requirements covering logbook records, service reports, training records, complaint records, and annual reports; and (h) Annual review and update process with procedures for amendments and Council certification.",
    status: "compliant",
    category: "Operation and Management Plan",
    additionalDetailsUrl: "/council/operations",
  },
  {
    id: "condition-15",
    conditionNumber: "Condition 15",
    title: "OMP Updates and Amendments",
    description: "The OMP may be updated or amended. Any amendments shall: (a) Only be for improving efficacy or emission controls; (b) Be consistent with consent conditions; and (c) Be submitted in writing to Canterbury Regional Council. Updates must be certified and made available onsite at all times.",
    managementMethod: "Documentation management - Any OMP updates are prepared in accordance with the conditions, submitted to Canterbury Regional Council for certification, and the certified version is maintained onsite at all times.",
    status: "compliant",
    category: "Operation and Management Plan",
  },
  // Maintenance, Record Keeping and Training
  {
    id: "condition-16",
    conditionNumber: "Condition 16",
    title: "Logbook Requirements",
    description: "A logbook must be maintained containing: date, start time, and duration; total weight of charge; type and number of animals; which cremator undertook cremation; temperature readings; comments on effectiveness; and be provided to Council on request.",
    managementMethod: "Digital logbook system - Our custom website serves as the logbook, automatically capturing all required data for each cremation cycle. When completing a batch, operators record: date, start time, and duration (automatically calculated); total weight of charge (validated against 50kg limit); type and number of animals (species breakdown tracked); which cremator undertook cremation (machine identification); first and second chamber temperature readings (validated against 750°C and 850°C minimums); opacity readings (Ringelmann Shade); and effectiveness comments. All records are stored digitally, retained permanently, and can be exported for Canterbury Regional Council on request. Data is also included in the Annual Report (Condition 24).",
    status: "compliant",
    category: "Maintenance, Record Keeping and Training",
  },
  {
    id: "condition-17",
    conditionNumber: "Condition 17",
    title: "Annual Servicing Requirements",
    description: "The cremator must be serviced at least once every year by a competent person, including: (a) Removal of all ash; (b) Cleaning the flue; (c) Checking and repairing the machine; and (d) Monitoring oxygen concentration in the flue.",
    managementMethod: "Service Reports Management System - All annual servicing is conducted by ComMec Industrial Services Ltd, a New Zealand-based company specialising in industrial incineration and cremation systems. Servicing includes initial commissioning, scheduled preventative maintenance per manufacturer specifications (Inciner8 Ltd, UK), inspection and testing of burners, combustion chambers, fuel lines, exhaust systems, and control panels. All annual servicing is recorded through our Service Reports system in the admin dashboard. Each service report captures: service date and provider (competent person); confirmation of ash removal; flue cleaning; machine checks and any repairs made; and oxygen concentration monitoring results. The system tracks service status for each machine, showing when service is due or overdue. A servicing logbook is maintained onsite. Service reports are prepared, retained digitally, and provided to Council on request (Condition 20). Service data is also included in the Annual Report (Condition 24). Reference: Inciner8 Maintenance Manual (available via Operations Plan section (d)).",
    status: "compliant",
    category: "Maintenance, Record Keeping and Training",
    additionalDetailsUrl: "/council/operational-responses",
  },
  {
    id: "condition-18",
    conditionNumber: "Condition 18",
    title: "Operator Training Requirements",
    description: "An operator must have received training in optimal operation of the cremator prior to undertaking cremation, including: conditions of consent and OMP requirements; logbook location and completion; operating and maintenance procedures; and servicing requirements.",
    managementMethod: "Training program - All operators receive comprehensive training prior to operating the cremator, covering consent conditions, OMP requirements, logbook procedures, operating and maintenance procedures, and servicing requirements. Training records are prepared, retained, and provided to Council on request. Reference: Inciner8 Maintenance Manual (available via Operations Plan section (d)).",
    status: "compliant",
    category: "Maintenance, Record Keeping and Training",
  },
  {
    id: "condition-19",
    conditionNumber: "Condition 19",
    title: "Training Records",
    description: "Training records shall be prepared and retained, and copies shall be provided to the Canterbury Regional Council on request.",
    managementMethod: "Record keeping - Training records for all operators are maintained and provided to Canterbury Regional Council on request.",
    status: "compliant",
    category: "Maintenance, Record Keeping and Training",
  },
  {
    id: "condition-20",
    conditionNumber: "Condition 20",
    title: "Service Reports",
    description: "Service reports shall be prepared and retained, and copies shall be provided to the Canterbury Regional Council on request.",
    managementMethod: "Digital record system - All service reports are created and stored in the Service Reports Management System. Reports can be viewed, searched, and filtered by machine or date. All service reports are retained digitally and can be exported or provided to Canterbury Regional Council on request. Service report data is also included in the Annual Report (Condition 24).",
    status: "compliant",
    category: "Maintenance, Record Keeping and Training",
  },
  // Exercise
  {
    id: "condition-21",
    conditionNumber: "Condition 21",
    title: "Notification of First Exercise",
    description: "The Canterbury Regional Council must be informed within five days of the first exercise of this consent by the consent holder.",
    managementMethod: "Administrative - Notification of first exercise was provided to Canterbury Regional Council within the required timeframe.",
    status: "compliant",
    category: "Exercise",
  },
  // Complaints
  {
    id: "condition-22",
    conditionNumber: "Condition 22",
    title: "Complaint Records",
    description: "A record of any complaints relating to particulate matter or odour shall be maintained and include: location where detected; date and time; wind speed and direction; likely cause; and corrective action undertaken.",
    managementMethod: "Environmental Complaints Management System - All complaints are recorded through our Environmental Complaints system in the admin dashboard. Each complaint captures: complaint type (odour or particulate matter); location where detected; date and time detected; wind speed and direction; likely cause; corrective action undertaken; complainant information (optional); and additional notes. All complaints are searchable and filterable, with full audit trail. Complaint records are retained digitally and included in the Annual Report (Condition 24).",
    status: "compliant",
    category: "Complaints",
  },
  {
    id: "condition-23",
    conditionNumber: "Condition 23",
    title: "Complaint Response and Notification",
    description: "All complaints must: (a) Be responded to as soon as practicable with records kept; (b) Be notified to Canterbury Regional Council immediately by the next working day; and (c) Be included in the annual report (Condition 24).",
    managementMethod: "System-managed workflow - The Environmental Complaints system tracks the full complaint lifecycle: (a) Responses to complainants are recorded with timestamps; (b) A 'Notify Council' function marks complaints as notified to Canterbury Regional Council with notification date/time; (c) All complaints are automatically included in the Annual Report (Condition 24). The system provides clear status indicators showing which complaints have been responded to and which have been notified to Council.",
    status: "compliant",
    category: "Complaints",
  },
  // Annual Report
  {
    id: "condition-24",
    conditionNumber: "Condition 24",
    title: "Annual Report",
    description: "An Annual Report must be provided within 28 days of request or submitted by 1 August each year, reporting on 1 July to 30 June, containing monitoring results and information from Conditions 16, 17, 18, and 23.",
    managementMethod: "Automated Annual Report Generation - The Annual Report system in the admin dashboard automatically compiles all required data for the reporting period (1 July to 30 June). The system generates comprehensive reports including: (Condition 16) Complete logbook data with all cremation batches, temperatures, weights, animal types, and effectiveness notes; (Condition 17) All service reports with servicing details; (Condition 18) Training records and certificates; (Condition 23) All complaints with responses and Council notifications. Reports can be generated for any reporting year, viewed online, and exported in JSON format. Reports are prepared and submitted to Canterbury Regional Council by 1 August each year.",
    status: "compliant",
    category: "Annual Report",
  },
  // Administration
  {
    id: "condition-25",
    conditionNumber: "Condition 25",
    title: "Review of Conditions",
    description: "The Canterbury Regional Council may, once per year on the last five working days of March or September, serve notice of intention to review conditions for dealing with adverse effects or requiring best practicable option.",
    managementMethod: "Administrative - This is a Council administrative condition. We will respond to any review notices served by Canterbury Regional Council in accordance with the Resource Management Act.",
    status: "compliant",
    category: "Administration",
  },
  {
    id: "condition-26",
    conditionNumber: "Condition 26",
    title: "Lapsing Date",
    description: "The lapsing date for the purposes of section 125 of the Resource Management Act shall be 19 December 2030.",
    managementMethod: "Administrative - This is an administrative condition setting the consent lapsing date. The consent must be exercised before 19 December 2030.",
    status: "compliant",
    category: "Administration",
  },
]

export default function EnvironmentalCompliancePage() {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem("councilSessionToken")
      
      if (!sessionToken) {
        router.push("/council/login")
        return
      }

      try {
        const response = await fetch("/api/council/verify", {
          headers: { "Authorization": `Bearer ${sessionToken}` },
        })

        if (!response.ok) {
          localStorage.removeItem("councilSessionToken")
          router.push("/council/login")
          return
        }

        setIsAuthenticated(true)
        
        // Track page access
        try {
          await fetch("/api/council/activity", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ 
              page: "/council/environmental",
              action: "council_environmental_view"
            }),
          })
        } catch (error) {
          console.error("Error tracking activity:", error)
        }
      } catch (error) {
        console.error("Auth verification error:", error)
        localStorage.removeItem("councilSessionToken")
        router.push("/council/login")
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [router])

  const getStatusBadge = (status: ComplianceCondition["status"]) => {
    switch (status) {
      case "compliant":
        return <Badge className="bg-green-100 text-green-800 border-green-300">Compliant</Badge>
      case "in-progress":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">In Progress</Badge>
      case "pending":
        return <Badge className="bg-gray-100 text-gray-800 border-gray-300">Pending</Badge>
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b0861] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push("/council/dashboard")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#5b0861]">PetAftercare</h1>
                <p className="text-sm text-gray-600">Environmental Compliance</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Related Reports Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Related Reports</CardTitle>
            <CardDescription>
              Access detailed compliance records and reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link href="/council/logbook">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#5b0861] hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#5b0861] bg-opacity-10 rounded-lg">
                      <FileText className="h-5 w-5 text-[#5b0861]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Logbook Records</h3>
                      <p className="text-xs text-gray-600">Cremation cycle records (Condition 16)</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                    View
                  </Button>
                </div>
              </Link>

              <Link href="/council/service-reports">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#5b0861] hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#5b0861] bg-opacity-10 rounded-lg">
                      <Wrench className="h-5 w-5 text-[#5b0861]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Service Reports</h3>
                      <p className="text-xs text-gray-600">Annual servicing records (Condition 17)</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                    View
                  </Button>
                </div>
              </Link>

              <Link href="/council/training-records">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#5b0861] hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#5b0861] bg-opacity-10 rounded-lg">
                      <BookOpen className="h-5 w-5 text-[#5b0861]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Training Records</h3>
                      <p className="text-xs text-gray-600">Operator training certificates (Condition 18)</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                    View
                  </Button>
                </div>
              </Link>

              <Link href="/council/complaints">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#5b0861] hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#5b0861] bg-opacity-10 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-[#5b0861]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Complaints</h3>
                      <p className="text-xs text-gray-600">Environmental complaint records (Condition 23)</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                    View
                  </Button>
                </div>
              </Link>

              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-75">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-300 rounded-lg">
                    <FileText className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-1">Notification of First Exercise</h3>
                    <p className="text-xs text-gray-500">To be determined</p>
                  </div>
                </div>
                <Badge variant="outline" className="flex-shrink-0">
                  TBD
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance Management Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-[#5b0861]" />
              <CardTitle>Compliance Management</CardTitle>
            </div>
            <CardDescription>
              How we are managing compliance for each resource consent condition
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {(() => {
                const categories = Array.from(new Set(complianceConditions.map(c => c.category || "Other")))
                return categories.map((category) => {
                  const conditionsInCategory = complianceConditions.filter(c => (c.category || "Other") === category)
                  return (
                    <div key={category} className="space-y-4">
                      <h2 className="text-xl font-bold text-[#5b0861] border-b border-gray-200 pb-2">
                        {category}
                      </h2>
                      <div className="space-y-4">
                        {conditionsInCategory.map((condition) => (
                          <div
                            key={condition.id}
                            className="p-6 border border-gray-200 rounded-lg hover:border-[#5b0861] transition-colors bg-white"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-gray-900 text-lg">
                                    {condition.conditionNumber}: {condition.title}
                                  </h3>
                                  {getStatusBadge(condition.status)}
                                </div>
                                <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{condition.description}</p>
                              </div>
                            </div>
                  <div className="bg-blue-50 border-l-4 border-[#5b0861] p-4 rounded">
                    <p className="text-sm font-medium text-gray-900 mb-1">Compliance Management:</p>
                    <p className="text-sm text-gray-700 mb-2" dangerouslySetInnerHTML={{
                      __html: condition.managementMethod.replace(
                        /(https?:\/\/[^\s]+)/g,
                        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-[#5b0861] hover:underline">$1</a>'
                      )
                    }}></p>
                    {condition.additionalDetailsUrl && (
                      <a
                        href={condition.additionalDetailsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#5b0861] hover:underline inline-flex items-center gap-1"
                      >
                        View additional details
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })
              })()}
              
              {complianceConditions.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No compliance conditions have been added yet.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
